/**
 * SurveyActions Component
 *
 * Reusable action button bar displayed in the header of all survey creation/editing pages.
 * Provides consistent primary and secondary actions across all survey types.
 *
 * ## Used By
 * - CustomSurvey
 * - EventRegistrationSurvey
 * - SatisfactionSurvey
 * - DonationSurvey
 *
 * ## Features
 * - **Save Button**: Primary action (form submission)
 * - **Copy Link Button**: Copies survey anonymous link to clipboard
 * - **QR Code Button**: Opens QR code modal for survey link
 * - **Loading States**: Disables actions during save operations
 * - **Conditional Rendering**: Shows/hides actions based on survey state
 * - **Fallback Clipboard**: Uses execCommand for older browsers
 *
 * ## Button Visibility Logic
 * - **Save**: Always visible (primary action)
 * - **Copy Link**: Visible when `showCopyLink=true` AND `surveyLink` exists
 * - **QR Code**: Visible when `onQRCodeClick` provided AND `surveyLink` exists
 *
 * ## Layout
 * Uses semantic `<menu>` element with flexbox:
 * - Horizontal layout by default
 * - Buttons displayed left-to-right: Copy Link | QR Code | Save
 * - Save button is rightmost (primary action position)
 * - Compact variant available for mobile/tight spaces
 *
 * ## Integration with Form
 * This component should be placed inside a `<form>` element:
 * - Save button uses `type='submit'` to trigger form submission
 * - Parent form's `onSubmit` handler performs actual save logic
 * - Loading state comes from mutation/save operation status
 */

import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../../components-v2/Button';
import { THEME } from '../../../../../components-v2/Theme/theme';

const { spacing } = THEME;

/**
 * Survey type identifier used for routing to correct reporting page
 */
type SurveyType = 'SatisfactionSurvey' | 'CustomSurvey' | 'EventRegistrationSurvey' | 'DonationSurvey';

/**
 * Maps survey type to reporting URL path segment
 */
const SURVEY_REPORTING_ROUTES: Record<SurveyType, string> = {
	SatisfactionSurvey: 'satisfaction',
	CustomSurvey: 'custom-survey-reports',
	EventRegistrationSurvey: 'event-reports',
	DonationSurvey: 'donations',
};

/**
 * Constructs the reporting URL for a given survey type and ID
 */
const getReportingUrl = (surveyType: SurveyType, surveyId: string): string => {
	const routeSegment = SURVEY_REPORTING_ROUTES[surveyType];
	return `/#/reporting/surveys/${routeSegment}/${surveyId}`;
};

/**
 * Props for SurveyActions component
 */
interface SurveyActionsProps {
	/**
	 * Custom handler for copy link action
	 *
	 * If provided, this handler is called when user clicks "Copy to Clipboard".
	 * If not provided, default behavior copies `surveyLink` to clipboard automatically.
	 *
	 * Use cases for custom handler:
	 * - Show custom toast notifications
	 * - Track analytics events
	 * - Add additional logic after copying
	 *
	 * @example
	 * ```tsx
	 * <SurveyActions
	 *   onCopyLink={() => {
	 *     // Custom logic here
	 *     toast.success('Survey link copied!');
	 *   }}
	 * />
	 * ```
	 */
	onCopyLink?: () => void;

	/**
	 * Handler for preview action (currently unused)
	 *
	 * Reserved for future preview functionality.
	 * Most surveys now use Preview tab instead of separate button.
	 */
	onPreview?: () => void;

	/**
	 * Loading state during save operation
	 *
	 * When true:
	 * - Save button shows loading spinner
	 * - All buttons are disabled
	 * - Prevents duplicate submissions
	 *
	 * Should be set from mutation status:
	 * ```tsx
	 * isLoading={saveSurveyMutation.isLoading}
	 * ```
	 */
	isLoading?: boolean;

	/**
	 * Disables the save button
	 *
	 * Use cases:
	 * - Form has validation errors
	 * - User is editing survey name inline
	 * - Required fields are empty
	 * - Survey is in invalid state
	 *
	 * Example:
	 * ```tsx
	 * isSaveDisabled={!formState.isValid || showEditSurveyName}
	 * ```
	 */
	isSaveDisabled?: boolean;

	/**
	 * Shows preview button (deprecated)
	 *
	 * Legacy prop - most surveys now use Preview tab instead.
	 * Kept for backwards compatibility.
	 *
	 * @deprecated Use Preview tab in TabRoot instead
	 */
	showPreview?: boolean;

	/**
	 * Shows "Copy to Clipboard" button
	 *
	 * When true AND surveyLink exists, displays copy link button.
	 * Typically shown only for saved surveys (not during creation).
	 *
	 * Logic:
	 * ```tsx
	 * showCopyLink={!!survey?.id}
	 * ```
	 */
	showCopyLink?: boolean;

	/**
	 * Survey's anonymous/public link
	 *
	 * The URL that gets copied to clipboard.
	 * Only exists for saved surveys that have been published.
	 *
	 * Format: Usually includes `/s/` segment (e.g., https://app.levitate.ai/s/abc123)
	 *
	 * Required for:
	 * - Copy link button to work
	 * - QR code button to be enabled
	 */
	surveyLink?: string;

	/**
	 * Additional CSS class name
	 *
	 * Applied in addition to default styles, allowing:
	 * - Survey-specific positioning
	 * - Custom spacing
	 * - Layout adjustments
	 */
	className?: string;

	/**
	 * Custom text for save button
	 *
	 * @default "Save"
	 *
	 * Common alternatives:
	 * - "Create Survey" (during creation)
	 * - "Update Survey" (during editing)
	 * - "Save Changes"
	 */
	saveText?: string;

	/**
	 * Custom text for copy link button
	 *
	 * @default "Copy to Clipboard"
	 *
	 * Alternative: "Copy Survey Link"
	 */
	copyLinkText?: string;

	/**
	 * Handler for QR code button click
	 *
	 * When provided, shows QR Code button that opens modal with QR code.
	 * Button is disabled if surveyLink doesn't exist.
	 *
	 * Typical usage:
	 * ```tsx
	 * onQRCodeClick={() => setShowQRCodeModal(true)}
	 * ```
	 */
	onQRCodeClick?: () => void;

	/**
	 * Survey ID used for View Report navigation
	 *
	 * Required when showViewReport is true. Used to construct the reporting URL.
	 */
	surveyId?: string;

	/**
	 * Shows "View Report" button
	 *
	 * When true AND surveyId exists, displays button that opens survey report in new tab.
	 * Typically shown only for saved surveys (not during creation).
	 *
	 * Logic:
	 * ```tsx
	 * showViewReport={!!survey?.id}
	 * ```
	 */
	showViewReport?: boolean;

	/**
	 * Survey type for determining correct reporting route
	 *
	 * Required when showViewReport is true. Used to construct the reporting URL.
	 */
	surveyType?: SurveyType;
}

/**
 * SurveyActions component
 *
 * Renders action buttons for survey management.
 *
 * @example Basic usage
 * ```tsx
 * <SurveyActions
 *   isLoading={saveMutation.isLoading}
 *   isSaveDisabled={!formState.isValid}
 *   showCopyLink={!!survey?.id}
 *   surveyLink={survey?.anonymousLink}
 *   onQRCodeClick={() => setShowQRModal(true)}
 * />
 * ```
 *
 * @example With custom handlers and text
 * ```tsx
 * <SurveyActions
 *   isLoading={isLoading}
 *   isSaveDisabled={showEditSurveyName}
 *   saveText="Update Survey"
 *   copyLinkText="Copy Survey Link"
 *   showCopyLink={!!survey?.id}
 *   surveyLink={survey?.anonymousLink}
 *   onCopyLink={() => {
 *     navigator.clipboard.writeText(survey.anonymousLink);
 *     toast.success('Survey link copied!');
 *   }}
 *   onQRCodeClick={() => setShowQRModal(true)}
 * />
 * ```
 */
export const SurveyActions: React.FC<SurveyActionsProps> = ({
	onCopyLink,
	onPreview: _onPreview, // Prefixed with _ to indicate intentionally unused
	isLoading = false,
	isSaveDisabled = false,
	showPreview: _showPreview = false, // Legacy prop, kept for backwards compatibility
	showCopyLink = false,
	surveyLink,
	className,
	saveText = 'Save',
	copyLinkText = 'Copy to Clipboard',
	onQRCodeClick,
	surveyId,
	showViewReport = false,
	surveyType,
}) => {
	/**
	 * Handles copying survey link to clipboard
	 *
	 * Flow:
	 * 1. If custom onCopyLink provided, call it (allows custom logic)
	 * 2. Otherwise, use default clipboard behavior
	 * 3. Try modern navigator.clipboard API first
	 * 4. Fall back to execCommand for older browsers (IE11, older mobile)
	 *
	 * Error handling:
	 * - Logs errors but doesn't show user-facing messages
	 * - Falls back gracefully to execCommand if clipboard API fails
	 */
	const handleCopyLink = React.useCallback(async () => {
		// Custom handler takes precedence (allows parent to show toasts, track analytics, etc.)
		if (onCopyLink) {
			onCopyLink();
			return;
		}

		// Default clipboard behavior
		if (surveyLink) {
			try {
				// Modern clipboard API (works in secure contexts - HTTPS, localhost)
				await navigator.clipboard.writeText(surveyLink);
				console.log('Survey link copied to clipboard');
				// Note: Parent components should show toast notifications
			} catch (error) {
				console.error('Failed to copy survey link:', error);

				// Fallback for older browsers or insecure contexts
				// Creates temporary textarea, selects text, copies, then removes element
				const textArea = document.createElement('textarea');
				textArea.value = surveyLink;
				textArea.style.position = 'fixed'; // Prevent scrolling
				textArea.style.opacity = '0'; // Hide from view
				document.body.appendChild(textArea);
				textArea.select();
				document.execCommand('copy'); // Legacy copy command
				document.body.removeChild(textArea);
			}
		}
	}, [onCopyLink, surveyLink]);

	/**
	 * Opens the survey report in a new tab
	 *
	 * Only callable when surveyId and surveyType are both available.
	 * Uses window.open with '_blank' target to open in new tab.
	 */
	const handleViewReport = React.useCallback(() => {
		if (surveyId && surveyType) {
			window.open(getReportingUrl(surveyType, surveyId), '_blank');
		}
	}, [surveyId, surveyType]);

	return (
		<menu
			className={className ? `${css(styles.formMenu)} ${className}` : css(styles.formMenu)}
			data-testid='survey-actions'
		>
			{/* Copy Link Button - Secondary action */}
			{/* Only visible when: survey has been saved AND parent passes showCopyLink=true */}
			{showCopyLink && surveyLink && (
				<li>
					<Button
						type='button' // Not a form submission
						variant='link' // Link styling (less prominent than primary)
						onClick={handleCopyLink}
						disabled={isLoading} // Disable during save operations
						data-testid='copy-link-btn'
					>
						{copyLinkText}
					</Button>
				</li>
			)}

			{/* QR Code Button - Secondary action */}
			{/* Only visible when: parent provides onQRCodeClick handler */}
			{/* Disabled when: surveyLink doesn't exist (survey not yet saved/published) */}
			{onQRCodeClick && (
				<li>
					<Button type='button' variant='link' onClick={onQRCodeClick} disabled={!surveyLink}>
						QR Code
					</Button>
				</li>
			)}

			{/* View Report Button - Secondary action */}
			{/* Only visible when: survey has been saved AND parent passes showViewReport=true */}
			{showViewReport && surveyId && surveyType && (
				<li>
					<Button
						type='button'
						variant='link'
						onClick={handleViewReport}
						disabled={isLoading}
						data-testid='view-report-btn'
					>
						View Report
					</Button>
				</li>
			)}

			{/* Save Button - Primary action (always visible) */}
			{/* Using type='submit' triggers parent form's onSubmit handler */}
			<li>
				<Button
					type='submit' // Triggers form submission
					variant='primary' // Primary button styling (prominent)
					disabled={isSaveDisabled || isLoading}
					isLoading={isLoading} // Shows spinner during save
					styles={[styles.submitButton]}
					data-testid='save-btn'
				>
					{saveText}
				</Button>
			</li>
		</menu>
	);
};

/**
 * Styles for SurveyActions component
 *
 * Uses flexbox for horizontal layout with consistent spacing.
 */
const styles = StyleSheet.create({
	formMenu: {
		display: 'flex', // Horizontal flexbox layout
		gap: spacing.md, // Consistent spacing between buttons (from design system)
		alignItems: 'center', // Vertically center buttons
	},
	submitButton: {
		// Empty style object reserved for future custom submit button styling
		// Currently uses Button component's default primary variant styles
	},
});

/**
 * SurveyActionsCompact Component
 *
 * Alternative layout for mobile or space-constrained scenarios.
 * Stacks buttons vertically instead of horizontally.
 *
 * ## Usage
 * Use this variant when:
 * - Viewport width is narrow (mobile devices)
 * - Survey header has limited horizontal space
 * - Design requires full-width action buttons
 *
 * ## Layout Differences
 * - Vertical stacking (column direction)
 * - Full-width buttons (stretch alignment)
 * - Tighter spacing (8px instead of spacing.md)
 *
 * @example
 * ```tsx
 * {isMobile ? (
 *   <SurveyActionsCompact {...actionProps} />
 * ) : (
 *   <SurveyActions {...actionProps} />
 * )}
 * ```
 */
export const SurveyActionsCompact: React.FC<SurveyActionsProps> = props => {
	return (
		<SurveyActions
			{...props}
			// Apply compact styles in addition to any custom className
			className={
				props.className ? `${css(compactStyles.compactMenu)} ${props.className}` : css(compactStyles.compactMenu)
			}
		/>
	);
};

/**
 * Styles for compact variant
 */
const compactStyles = StyleSheet.create({
	compactMenu: {
		flexDirection: 'column' as const, // Stack vertically instead of horizontally
		gap: '8px', // Tighter spacing for vertical layout
		alignItems: 'stretch', // Buttons take full width of container
	},
});

/**
 * Type exports for consumers
 */
export type { SurveyActionsProps, SurveyType };
